"use client";

import {
  Download,
  FileCode,
  FileDown,
  Link2,
  List,
  ListOrdered,
  NotebookPen,
  Printer,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { trackBibliographyExport } from "@/lib/analytics";
import {
  BIBLIOGRAPHY_FONTS,
  type BibliographyFont,
  countAnnotations,
  exportAsBibTeX,
  exportAsCslJson,
  exportAsHtml,
  exportAsRis,
  exportAsRtf,
  exportAsText,
  exportAsWordHTML,
  type SavedCitation,
} from "@/lib/bibliography";
import { writeToClipboardOrThrow } from "@/lib/clipboard";
import {
  buildShareUrl,
  encodeSharedBibliography,
} from "@/lib/share-bibliography";

type ExportKind = Parameters<typeof trackBibliographyExport>[0];

/** Trigger a browser download for generated text content. */
export function downloadTextFile(
  content: string,
  mimeType: string,
  extension: string,
  basename: string
) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${basename}.${extension}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface BibliographyExportMenuProps {
  /** The ordered, filtered list — exactly what the user sees is what they export. */
  citations: SavedCitation[];
  bibliographyName?: string;
  locale: string;
  align?: "start" | "end";
  /** Primary treatment for the studio header; outline everywhere else. */
  variant?: "default" | "outline";
  size?: "sm" | "default";
  /** Hide the label so the trigger is icon-only below `sm`. */
  responsiveLabel?: boolean;
}

/**
 * Every way a bibliography can leave the browser, in one menu: copy as a list, copy
 * as BibTeX/HTML, download Word/RTF/RIS/CSL-JSON, print, or mint a share link.
 *
 * Shared by the full-page studio and the compact dialog so the two surfaces can never
 * drift apart on which formats they offer.
 */
export function BibliographyExportMenu({
  citations,
  bibliographyName,
  locale,
  align = "end",
  variant = "outline",
  size = "sm",
  responsiveLabel = true,
}: BibliographyExportMenuProps) {
  const bib = useBibStrings();
  const [open, setOpen] = useState(false);
  const [font, setFont] = useState<BibliographyFont>("Times New Roman");
  const annotationCount = countAnnotations(citations);
  const basename = bibliographyName || "bibliography";

  async function copy(text: string, feedback: string, kind: ExportKind) {
    try {
      await writeToClipboardOrThrow(text);
      toast.success(feedback);
      trackBibliographyExport(kind);
    } catch {
      toast.error(bib.copyFailed);
    }
    setOpen(false);
  }

  function download(
    content: string,
    mimeType: string,
    extension: string,
    feedback: string,
    kind: ExportKind,
    name = basename
  ) {
    downloadTextFile(content, mimeType, extension, name);
    toast.success(feedback);
    trackBibliographyExport(kind);
    setOpen(false);
  }

  async function handleShareLink() {
    try {
      const token = await encodeSharedBibliography(
        citations,
        bibliographyName || bib.sharedTitle
      );
      const url = buildShareUrl(token, window.location.origin, locale);
      await copy(url, bib.shareCopied, "share-link");
    } catch {
      toast.error(bib.shareFailed);
      setOpen(false);
    }
  }

  function handlePrint() {
    const html = exportAsWordHTML(citations, bibliographyName, font);
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error(bib.printFailed);
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    trackBibliographyExport("print");
    setOpen(false);
  }

  const item = "w-full justify-start";

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button size={size} title={bib.exportTitle} variant={variant}>
          <Download className="size-4" />
          <span className={responsiveLabel ? "sr-only sm:not-sr-only" : ""}>
            {bib.export}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-60 p-2">
        <div className="space-y-1">
          <p className="px-2 py-1 font-medium text-muted-foreground text-xs">
            {bib.copyToClipboard}
          </p>
          <Button
            className={item}
            onClick={() =>
              copy(
                exportAsText(citations, true),
                bib.copiedNumbered,
                "numbered"
              )
            }
            size="sm"
            variant="ghost"
          >
            <ListOrdered className="me-2 size-4" />
            {bib.numberedList}
          </Button>
          <Button
            className={item}
            onClick={() =>
              copy(
                exportAsText(citations, false),
                bib.copiedBulleted,
                "bulleted"
              )
            }
            size="sm"
            variant="ghost"
          >
            <List className="me-2 size-4" />
            {bib.bulletedList}
          </Button>
          <Button
            className={item}
            onClick={() =>
              copy(exportAsBibTeX(citations), bib.copiedBibtex, "bibtex")
            }
            size="sm"
            variant="ghost"
          >
            <FileCode className="me-2 size-4" />
            BibTeX
          </Button>
          <Button
            className={item}
            onClick={() =>
              copy(exportAsHtml(citations), bib.copiedHtml, "html")
            }
            size="sm"
            variant="ghost"
          >
            <FileCode className="me-2 size-4" />
            {bib.htmlSnippet}
          </Button>

          {annotationCount > 0 ? (
            <>
              <hr className="my-2 border-border" />
              <p className="px-2 py-1 font-medium text-muted-foreground text-xs">
                {bib.copyWithAnnotations} ({annotationCount})
              </p>
              <Button
                className={item}
                onClick={() =>
                  copy(
                    exportAsText(citations, true, true),
                    bib.copiedAnnotated,
                    "annotated-text"
                  )
                }
                size="sm"
                variant="ghost"
              >
                <NotebookPen className="me-2 size-4" />
                {bib.copyWithAnnotations}
              </Button>
              <Button
                className={item}
                onClick={() =>
                  download(
                    exportAsWordHTML(
                      citations,
                      bibliographyName
                        ? `${bibliographyName} (annotated)`
                        : undefined,
                      font,
                      true
                    ),
                    "application/msword",
                    "doc",
                    bib.downloadedAnnotated,
                    "annotated-word"
                  )
                }
                size="sm"
                variant="ghost"
              >
                <FileDown className="me-2 size-4" />
                {bib.wordAnnotated}
              </Button>
            </>
          ) : null}
          <hr className="my-2 border-border" />

          <p className="px-2 py-1 font-medium text-muted-foreground text-xs">
            {bib.downloadFile}
          </p>
          <Button
            className={item}
            onClick={() =>
              download(
                exportAsWordHTML(citations, bibliographyName, font),
                "application/msword",
                "doc",
                bib.downloadedWord,
                "word"
              )
            }
            size="sm"
            variant="ghost"
          >
            <FileDown className="me-2 size-4" />
            {bib.wordDocument}
          </Button>
          <Button
            className={item}
            onClick={() =>
              download(
                exportAsRis(citations),
                "application/x-research-info-systems",
                "ris",
                bib.downloadedRis,
                "ris"
              )
            }
            size="sm"
            variant="ghost"
          >
            <FileDown className="me-2 size-4" />
            RIS (.ris) — Zotero, Mendeley
          </Button>
          <Button
            className={item}
            onClick={() =>
              download(
                exportAsCslJson(citations),
                "application/json",
                "json",
                bib.downloadedCsl,
                "csljson"
              )
            }
            size="sm"
            variant="ghost"
          >
            <FileDown className="me-2 size-4" />
            CSL-JSON (.json) — Pandoc, Quarto
          </Button>
          <Button
            className={item}
            onClick={() =>
              download(
                exportAsRtf(citations, bibliographyName, font),
                "application/rtf",
                "rtf",
                bib.downloadedRtf,
                "rtf"
              )
            }
            size="sm"
            variant="ghost"
          >
            <FileDown className="me-2 size-4" />
            Rich Text (.rtf) — Docs, Pages
          </Button>

          <hr className="my-2 border-border" />

          <Button
            className={item}
            onClick={handleShareLink}
            size="sm"
            variant="ghost"
          >
            <Link2 className="me-2 size-4" />
            {bib.copyShareLink}
          </Button>
          <Button
            className={item}
            onClick={handlePrint}
            size="sm"
            variant="ghost"
          >
            <Printer className="me-2 size-4" />
            {bib.print}
          </Button>

          <hr className="my-2 border-border" />

          <div className="flex items-center justify-between gap-2 px-2 py-1">
            <label
              className="font-medium text-muted-foreground text-xs"
              htmlFor="bibliography-export-font"
            >
              {bib.font}
            </label>
            <select
              className="rounded-md border border-border bg-background px-2 py-1 text-foreground text-xs"
              id="bibliography-export-font"
              onChange={(event) =>
                setFont(event.target.value as BibliographyFont)
              }
              value={font}
            >
              {BIBLIOGRAPHY_FONTS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
